import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, Printer, RefreshCw, ShieldCheck } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import api from '../../services/api';

const fmt = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(n || 0);
const ENTITY_MAP = { usa: 'Eyvo USA', india: 'Eyvo India' };

// ── Date helpers — Fiscal Year starts Jan 1 ───────────────────────────────────
const pad = (n) => String(n).padStart(2, '0');
const iso = (y, m, d) => `${y}-${pad(m)}-${pad(d)}`;
const lastDay = (y, m) => new Date(y, m, 0).getDate(); // m is 1-based

function getPLDates(preset) {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth() + 1;
  const d = now.getDate();
  const today = iso(y, m, d);

  switch (preset) {
    case 'today':
      return { from: today, to: today };
    case 'this_month':
      return { from: iso(y, m, 1), to: today };
    case 'last_month': {
      const lm = m === 1 ? 12 : m - 1;
      const ly = m === 1 ? y - 1 : y;
      return { from: iso(ly, lm, 1), to: iso(ly, lm, lastDay(ly, lm)) };
    }
    case 'last_quarter': {
      const q = Math.ceil(m / 3);
      const pq = q === 1 ? 4 : q - 1;
      const py = q === 1 ? y - 1 : y;
      const qStarts = { 1: [1, 1], 2: [4, 1], 3: [7, 1], 4: [10, 1] };
      const qEnds   = { 1: [3, 31], 2: [6, 30], 3: [9, 30], 4: [12, 31] };
      return {
        from: iso(py, qStarts[pq][0], qStarts[pq][1]),
        to:   iso(py, qEnds[pq][0],   qEnds[pq][1]),
      };
    }
    case 'ytd':
      return { from: iso(y, 1, 1), to: today };
    case 'last_fy':
      return { from: iso(y - 1, 1, 1), to: iso(y - 1, 12, 31) };
    default:
      return null;
  }
}

const PL_PRESETS = [
  { value: 'today',        label: 'Today' },
  { value: 'this_month',   label: 'This Month to Date' },
  { value: 'last_month',   label: 'End of Last Month' },
  { value: 'last_quarter', label: 'Last Quarter' },
  { value: 'ytd',          label: 'Year to Date' },
  { value: 'last_fy',      label: 'Last Fiscal Year' },
  { value: 'custom',       label: 'Custom Range…' },
];

function Section({ title, rows, total, totalLabel, isPositive }) {
  return (
    <div className="mb-0">
      <div className="px-6 py-3 bg-slate-50 border-b border-slate-200">
        <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider">{title}</h3>
      </div>
      {rows.length === 0 ? (
        <div className="px-6 py-3 text-xs text-slate-400 italic">No entries</div>
      ) : (
        rows.map(r => (
          <div key={r.account_id} className="flex items-center justify-between px-6 py-2.5 hover:bg-slate-50/50 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <span className="font-mono text-xs text-slate-400 w-14">{r.account_code}</span>
              <span className="text-sm text-slate-700">{r.account_name}</span>
            </div>
            <span className={`font-mono text-sm font-medium ${isPositive ? 'text-emerald-700' : 'text-red-600'}`}>
              {fmt(r.balance)}
            </span>
          </div>
        ))
      )}
      <div className="flex items-center justify-between px-6 py-3 bg-slate-50 border-b border-slate-200">
        <span className="text-sm font-semibold text-slate-800">{totalLabel}</span>
        <span className={`font-mono text-sm font-bold ${isPositive ? 'text-emerald-700' : 'text-red-600'}`}>{fmt(total)}</span>
      </div>
    </div>
  );
}

export default function PLReport() {
  const todayStr = new Date().toISOString().slice(0, 10);
  const { pathname } = useLocation();
  const entitySlug = pathname.split('/')[2];
  const lockedEntity = ENTITY_MAP[entitySlug] || null;

  const [preset, setPreset]     = useState('today');
  const [dateFrom, setDateFrom] = useState(todayStr);
  const [dateTo, setDateTo]     = useState(todayStr);
  const [entity, setEntity]     = useState(lockedEntity || 'All');
  const [basis, setBasis]       = useState('cash');
  const [report, setReport]     = useState(null);
  const [loading, setLoading]   = useState(false);

  const runReport = async (from, to, ent, bas) => {
    setLoading(true);
    try {
      const res = await api.get(`/api/accounts/reports/pl?date_from=${from}&date_to=${to}&entity=${ent}&basis=${bas}`);
      setReport(res.data);
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to generate report');
    }
    setLoading(false);
  };

  // Auto-run Today on mount (cash basis default)
  useEffect(() => {
    const dates = getPLDates('today');
    runReport(dates.from, dates.to, lockedEntity || 'All', 'cash');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePresetChange = (val) => {
    setPreset(val);
    if (val !== 'custom') {
      const dates = getPLDates(val);
      setDateFrom(dates.from);
      setDateTo(dates.to);
      runReport(dates.from, dates.to, entity, basis);
    }
  };

  const handleEntityChange = (val) => {
    setEntity(val);
    if (preset !== 'custom') {
      const dates = getPLDates(preset);
      if (dates) runReport(dates.from, dates.to, val, basis);
    }
  };

  const handleBasisChange = (val) => {
    setBasis(val);
    if (preset !== 'custom') {
      const dates = getPLDates(preset);
      if (dates) runReport(dates.from, dates.to, entity, val);
    } else {
      runReport(dateFrom, dateTo, entity, val);
    }
  };

  const presetLabel = PL_PRESETS.find(p => p.value === preset)?.label || '';

  return (
    <div className="p-6 fade-in" data-testid="pl-report-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Profit &amp; Loss
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Income vs Expenses — {presetLabel}</p>
        </div>
        {report && (
          <button
            onClick={() => window.print()}
            className="inline-flex items-center gap-1.5 px-3 py-2 border border-slate-200 rounded-md text-sm font-medium hover:bg-slate-50"
            data-testid="print-pl-btn"
          >
            <Printer size={14} /> Print
          </button>
        )}
      </div>

      {/* Controls */}
      <div className="flex gap-3 items-end mb-6 flex-wrap bg-white border border-slate-200 rounded-lg p-4">
        {/* Cash / Accrual toggle */}
        <div>
          <label className="text-xs text-slate-500 mb-1 block">Accounting Basis</label>
          <div className="flex rounded-md border border-slate-200 overflow-hidden" data-testid="basis-toggle">
            <button
              onClick={() => handleBasisChange('cash')}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                basis === 'cash'
                  ? 'bg-indigo-900 text-white'
                  : 'bg-white text-slate-600 hover:bg-slate-50'
              }`}
              data-testid="basis-cash-btn"
            >
              Cash
            </button>
            <button
              onClick={() => handleBasisChange('accrual')}
              className={`px-4 py-2 text-sm font-medium border-l border-slate-200 transition-colors ${
                basis === 'accrual'
                  ? 'bg-indigo-900 text-white'
                  : 'bg-white text-slate-600 hover:bg-slate-50'
              }`}
              data-testid="basis-accrual-btn"
            >
              Accrual
            </button>
          </div>
        </div>

        <div>
          <label className="text-xs text-slate-500 mb-1 block">Report Period</label>
          <select
            value={preset}
            onChange={e => handlePresetChange(e.target.value)}
            className="border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-900"
            data-testid="pl-preset-select"
          >
            {PL_PRESETS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </div>

        {preset === 'custom' && (
          <>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">From</label>
              <input
                type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                className="border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-900"
              />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">To</label>
              <input
                type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                className="border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-900"
              />
            </div>
            <button
              onClick={() => runReport(dateFrom, dateTo, entity, basis)}
              disabled={loading}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-indigo-900 text-white text-sm font-medium rounded-md hover:bg-indigo-800 disabled:opacity-50"
              data-testid="generate-pl-btn"
            >
              {loading ? <><RefreshCw size={14} className="animate-spin" /> Generating…</> : 'Generate'}
            </button>
          </>
        )}

        {!lockedEntity && (
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Entity</label>
            <select
              value={entity}
              onChange={e => handleEntityChange(e.target.value)}
              className="border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-900"
              data-testid="pl-entity-select"
            >
              <option value="All">All Entities</option>
              <option value="Eyvo USA">Eyvo USA</option>
              <option value="Eyvo India">Eyvo India</option>
            </select>
          </div>
        )}

        {loading && preset !== 'custom' && (
          <div className="flex items-center gap-2 text-sm text-slate-500 self-end pb-2">
            <RefreshCw size={14} className="animate-spin" /> Generating…
          </div>
        )}
      </div>

      {/* Report Output */}
      {report && (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="pl-report-content">
          <div className="px-6 py-5 border-b border-slate-200 text-center">
            <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
              {report.entity === 'All' ? 'Consolidated' : report.entity} — Profit &amp; Loss
            </h2>
            <p className="text-sm text-slate-500 mt-1">{report.date_from} to {report.date_to}</p>
            <span className={`inline-flex items-center mt-2 px-3 py-0.5 rounded-full text-xs font-semibold ${
              report.basis === 'cash'
                ? 'bg-emerald-100 text-emerald-800'
                : 'bg-blue-100 text-blue-800'
            }`} data-testid="basis-label">
              {report.basis === 'cash' ? 'Cash Basis' : 'Accrual Basis'}
            </span>
            {report.reconciliation?.reconciled && (
              <span
                className="inline-flex items-center gap-1.5 ml-2 mt-2 px-3 py-0.5 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-800 border border-indigo-200"
                title={`Reconciled against ${report.reconciliation.source} · ${report.reconciliation.reconciled_at}`}
                data-testid="reconciled-badge"
              >
                <ShieldCheck size={12} />
                Xero Reconciled
              </span>
            )}
          </div>

          <Section title="Revenue" rows={report.revenue} total={report.total_revenue} totalLabel="Total Revenue" isPositive />
          <Section title="Cost of Goods Sold" rows={report.cogs} total={report.total_cogs} totalLabel="Total COGS" isPositive={false} />

          <div className={`flex items-center justify-between px-6 py-4 border-b-2 border-slate-300 ${report.gross_profit >= 0 ? 'bg-emerald-50' : 'bg-red-50'}`}>
            <span className="text-base font-bold text-slate-900">Gross Profit</span>
            <span className={`font-mono text-base font-bold ${report.gross_profit >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
              {fmt(report.gross_profit)}
            </span>
          </div>

          <Section title="Operating Expenses" rows={report.expenses} total={report.total_expenses} totalLabel="Total Expenses" isPositive={false} />

          <div className={`flex items-center justify-between px-6 py-5 ${report.net_income >= 0 ? 'bg-emerald-100' : 'bg-red-100'}`}>
            <div className="flex items-center gap-2">
              {report.net_income >= 0
                ? <TrendingUp size={18} className="text-emerald-700" />
                : <TrendingDown size={18} className="text-red-600" />}
              <span className="text-base font-bold text-slate-900">Net Income</span>
            </div>
            <span className={`font-mono text-xl font-bold ${report.net_income >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
              {fmt(report.net_income)}
            </span>
          </div>
        </div>
      )}

      {!report && !loading && (
        <div className="bg-white border border-slate-200 rounded-lg px-5 py-16 text-center text-slate-400 text-sm">
          Select a report period above
        </div>
      )}
    </div>
  );
}
